import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { type AvailableStandardizedSection } from '@heidi/integrations/ehr/contracts';
import { useTranslations } from '@heidi/runtime/i18n';
import { Check, Link2, Unlink } from 'lucide-react';
import { useIntl } from 'react-intl';

export const LinkEhrSectionDropdown = ({
  onOpenChange,
  ariaLabel,
  sections,
  linkedSectionId,
  onSelectSection,
  disabled = false,
}: {
  onOpenChange: (open: boolean) => void;
  ariaLabel: string;
  sections: AvailableStandardizedSection[];
  linkedSectionId: string | null;
  onSelectSection: (sectionId: string | null) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslations();
  const intl = useIntl();
  const linkedSection = sections.find((s) => s.section_id === linkedSectionId);
  const unlinkSectionLabel = intl.formatMessage({
    defaultMessage: 'Unlink EHR section',
    id: 'WbNrONrV+e',
    description:
      'Dropdown item label to remove the link between a note section and an EHR section',
  });
  const triggerLabel =
    linkedSection?.name ??
    linkedSection?.section_id ??
    (linkedSectionId ? t('common.vMarkdown.section.unknownSection') : null) ??
    t('common.vMarkdown.section.linkEhrFallbackLabel');

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          onMouseDown={(event) => event.preventDefault()}
          size="xs"
          className="p-1"
          variant="ghost"
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          data-testid="link-ehr-section-trigger"
        >
          {triggerLabel}
          <Link2 size={12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {sections.map((section) => {
          const isActive = linkedSectionId === section.section_id;
          return (
            <DropdownMenuItem
              key={section.section_id}
              onMouseDown={(event) => event.preventDefault()}
              onSelect={() => onSelectSection(isActive ? null : section.section_id)}
              data-testid={`link-ehr-section-${section.section_id}`}
            >
              {section.name ?? section.section_id}
              {isActive && <Check size={14} className="ms-auto" />}
            </DropdownMenuItem>
          );
        })}
        {linkedSectionId && (
          <>
            {sections.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onMouseDown={(event) => event.preventDefault()}
              onSelect={() => onSelectSection(null)}
              data-testid="unlink-ehr-section"
            >
              <Unlink size={14} />
              {unlinkSectionLabel}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
